import { parseSorting, serializeSorting } from '@rio-cloud/rio-uikit/utils/routeUtils';
import { naturalSortByProperty, type SortDirectionType } from '@rio-cloud/rio-uikit/utils/SortUtils';
import { useMemo } from 'react';

import { stringParser, useSearchParam } from './useSearchParam.ts';

const DEFAULT_SORT_DIRECTION: SortDirectionType = 'asc';

const getResolvedSortState = <TSortKey extends string>(
    sortParam: string | null,
    defaultSortKey: TSortKey,
    sortableKeys: readonly TSortKey[]
) => {
    const parsedSorting = parseSorting(sortParam);
    const parsedSortKey = parsedSorting?.sortBy as TSortKey | undefined;

    if (!parsedSortKey || !sortableKeys.includes(parsedSortKey)) {
        return {
            sortKey: defaultSortKey,
            sortDirection: DEFAULT_SORT_DIRECTION,
        };
    }

    return {
        sortKey: parsedSortKey,
        sortDirection: parsedSorting?.sortDir ?? DEFAULT_SORT_DIRECTION,
    };
};

const sortTableData = <T, TSortKey extends Extract<keyof T, string>>(
    data: T[],
    fixedSortKey: keyof T,
    secondarySortKey: TSortKey,
    sortDirection: SortDirectionType
) => {
    const primarySortedItems = naturalSortByProperty([...data], fixedSortKey, sortDirection);
    const groupedItems = primarySortedItems.reduce<Record<string, T[]>>((accumulator, item) => {
        const groupKey = String(item[fixedSortKey]);

        if (!accumulator[groupKey]) {
            accumulator[groupKey] = [];
        }

        accumulator[groupKey].push(item);
        return accumulator;
    }, {});

    return Object.values(groupedItems).flatMap(group =>
        naturalSortByProperty([...group], secondarySortKey, sortDirection)
    );
};

export const useTableSorting = <T, TSortKey extends Extract<keyof T, string>>(
    data: T[],
    fixedSortKey: keyof T,
    defaultSecondarySortKey: TSortKey,
    sortableKeys: readonly TSortKey[],
    sortParamKey = 'sort'
) => {
    const [sortParam, setSortParam] = useSearchParam(sortParamKey, stringParser);
    const { sortKey, sortDirection } = useMemo(
        () => getResolvedSortState(sortParam, defaultSecondarySortKey, sortableKeys),
        [defaultSecondarySortKey, sortParam, sortableKeys]
    );

    const sortedItems = useMemo(
        () => sortTableData(data, fixedSortKey, sortKey, sortDirection),
        [data, fixedSortKey, sortDirection, sortKey]
    );

    const handleSortByColumn = (column: keyof T, nextSortDirection?: SortDirectionType) => {
        const columnKey = column as TSortKey;
        const resolvedSortDirection =
            nextSortDirection ?? (columnKey === sortKey && sortDirection === 'asc' ? 'desc' : 'asc');

        setSortParam(
            serializeSorting({
                sortBy: columnKey,
                sortDir: resolvedSortDirection,
            })
        );
    };

    return { sortedItems, sortKey, sortDirection, handleSortByColumn };
};
